import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BannerNotificationsService, SpinnerService } from '../../../xform-compat';
import { AlertsDataService } from '../../../core/data-services/alerts-data.service';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { AuthorizationService } from '../../../core/services/authorization.service';
import { TemplateModalService } from '../../../core/services/template-modal.service';
import { PERMISSIONS } from '../../enums/permissions.enum';
import { CONFIG } from '../../../config';
import { Pagination, MyAlertSummary } from '../../models';
import { AlertFormComponent } from '../alert-form/alert-form.component';

/**
 * Shared alert list component used across the my-alerts page and company-alerts contexts.
 * Manages its own data fetching, pagination, filtering, deletion, and assign/unassign actions.
 * When companyId is provided, alerts are scoped to that company.
 */
@Component({
    selector: 'rms-alert-list',
    templateUrl: './alert-list.component.html',
    styleUrls: ['./alert-list.component.scss'],
    standalone: false
})
export class AlertListComponent implements OnInit, AfterViewInit, OnDestroy {

  /** When provided, filters alerts to this company and hides the Company Name column. */
  @Input() companyId: number;

  /** Emitted after data is loaded with the total number of alerts. */
  @Output() alertCountChanged = new EventEmitter<number>();

  public alertList: Array<any> = [];
  public pagination: Pagination;
  public alertsSummary: MyAlertSummary;
  public showCompanyColumn = true;

  // Filter toggle states
  public alertsByMe = true;
  public alertsToMe = true;
  public showArchived = false;

  // Display config
  public formatEST = CONFIG.formatEST;
  public verboseDateFormat = CONFIG.verboseDateFormat;
  public timeFormat = CONFIG.timeOnly;

  // Permissions
  public permissions = {
    read: false,
    alertRead: false,
    alertCreate: false,
    alertUpdate: false,
    alertDelete: false
  };
  public loggedUserId: number;

  // Frequency lookup options
  public manualAlertFrequencyOptions = CONFIG.alertOptions.manualAlertOptions;
  public automaticAlertFrequencyOptions = CONFIG.alertOptions.automaticAlertOptions;

  // Translations for confirmation dialogs
  public translations: any = {};

  private initialized = false;
  private destroy$ = new Subject<void>();

  constructor(
    private alertsDataService: AlertsDataService,
    private spinnerService: SpinnerService,
    private bannerNotificationService: BannerNotificationsService,
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private translateService: TranslateService,
    private templateModalService: TemplateModalService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.showCompanyColumn = !this.companyId;
    this.loggedUserId = this.authenticationService.getLoggedUser()?.id;
    this.initPermissions();
    this.initPagination();
    this.translateAlertMsgs();
  }

  ngAfterViewInit(): void {
    // Defer the initial load to the next microtask to avoid
    // ExpressionChangedAfterItHasBeenCheckedError from the spinner
    // starting during the same change detection cycle.
    Promise.resolve().then(() => {
      this.initialized = true;
      this.loadAlerts();
      this.loadSummary();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads alerts from the API using current filter and pagination state.
   * Called internally after filter changes, pagination, and CRUD operations.
   */
  public loadAlerts(): void {
    // In the my-alerts context (no companyId), at least one user-scoped filter
    // must be active. If both are off, clear the list without making a request.
    if (!this.companyId && !this.alertsByMe && !this.alertsToMe) {
      this.alertList = [];
      this.alertCountChanged.emit(0);
      return;
    }

    this.spinnerService.start();
    const params = this.buildParams();
    this.alertsDataService.getAlerts(params)
      .pipe(
        finalize(() => this.spinnerService.stop()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data: any) => {
          const records = data?.records ?? data ?? [];
          this.alertList = this.postProcessAlerts(records);
          if (data?.totalRecords != null) {
            this.pagination.page = (params.page ?? 0) + 1;
          }
          if (data?.totalRecords != null) {
            this.alertCountChanged.emit(data.totalRecords);
          }
        },
        error: (error) => this.bannerNotificationService.error(error)
      });
  }

  /**
   * Loads alert summary counts (active and archived totals).
   * When companyId is set, counts are scoped to that company.
   */
  public loadSummary(): void {
    this.alertsDataService.getAlertsSummary(this.companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (summary) => this.alertsSummary = summary,
        error: (error) => this.bannerNotificationService.error(error)
      });
  }

  /**
   * Handles filter toggle changes for Created By Me and Assigned To Me.
   */
  public onFilterChange(): void {
    this.pagination.page = 1;
    this.loadAlerts();
    this.loadSummary();
  }

  /**
   * Handles the Show Archived toggle change.
   */
  public onShowArchivedChange(): void {
    this.pagination.page = 1;
    this.loadAlerts();
    this.loadSummary();
  }

  /**
   * Opens the alert form modal for editing an existing alert.
   */
  public onEdit(item: any): void {
    const modalRef = this.modalService.open(AlertFormComponent, {
      windowClass: 'alert-form-modal',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.companyId = item.company?.id ?? item.companyId;
    modalRef.componentInstance.alert = item;
    modalRef.componentInstance.isEditMode = true;
    modalRef.result.then(
      () => {
        this.loadAlerts();
        this.loadSummary();
      },
      () => { /* dismissed */ }
    );
  }

  /**
   * Deletes an alert after SweetAlert confirmation.
   */
  public deleteAlert(item: any): void {
    this.spinnerService.start();
    const companyId = item.company?.id ?? item.companyId;
    this.alertsDataService.deleteAlert(companyId, item.id)
      .pipe(
        finalize(() => this.spinnerService.stop()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.loadAlerts();
          this.loadSummary();
        },
        error: (error) => this.bannerNotificationService.error(error)
      });
  }

  /**
   * Assigns the current user to an alert.
   */
  public assignAlert(item: any): void {
    const companyId = item.company?.id ?? item.companyId;
    this.alertsDataService.assignAlert(companyId, item.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.bannerNotificationService.success(
            this.translateService.instant('MESSAGES.UPDATED', { value: 'Alert' })
          );
          this.loadAlerts();
          this.loadSummary();
        },
        error: (error) => this.bannerNotificationService.error(error)
      });
  }

  /**
   * Unassigns the current user from an alert (after SweetAlert confirmation).
   */
  public unassignAlert(item: any): void {
    const companyId = item.company?.id ?? item.companyId;
    this.alertsDataService.unassignAlert(companyId, item.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.bannerNotificationService.success(
            this.translateService.instant('MESSAGES.UPDATED', { value: 'Alert' })
          );
          this.loadAlerts();
          this.loadSummary();
        },
        error: (error) => this.bannerNotificationService.error(error)
      });
  }

  /**
   * Checks if the current user is assigned to the given alert.
   * Uses the pre-computed flag set during postProcessAlertFrequency.
   */
  public isCurrentUserAssigned(item: any): boolean {
    return item.isAssignedToMe ?? false;
  }

  /**
   * Generates the SweetAlert confirmation template HTML.
   */
  public templateModal(type: string, record: string, recordName: string): string {
    return this.templateModalService.getConfirmationTemplate(
      this.translations[`MESSAGES.CONFIRM_${type}`], record, recordName
    );
  }

  /**
   * Resolves a frequency value to its display name.
   */
  public getAlertFrequencyName(value: string): string {
    if (value == null || value === '') {
      return '';
    }
    const allOptions = this.manualAlertFrequencyOptions.concat(this.automaticAlertFrequencyOptions);
    const match = allOptions.find(opt => opt.value === value.toString());
    return match ? match.name : '';
  }

  /**
   * Handles pagination page change events.
   */
  public onPageChanged(): void {
    if (this.initialized) {
      this.loadAlerts();
    }
  }

  /**
   * Handles records-per-page change events.
   */
  public onRecordsPerPageChanged(): void {
    if (this.initialized) {
      this.pagination.page = 1;
      this.loadAlerts();
    }
  }

  /**
   * Returns the total records from the last API response for pagination display.
   */
  public get totalRecords(): number {
    return this.alertsSummary
      ? (this.showArchived
        ? (this.alertsSummary.activeTotal + this.alertsSummary.archivedTotal)
        : this.alertsSummary.activeTotal)
      : 0;
  }

  private initPermissions(): void {
    this.permissions = {
      read: this.authorizationService.hasPermission([PERMISSIONS.CoreCompanyRead]),
      alertRead: this.authorizationService.hasPermission([PERMISSIONS.CoreCompanyAlertRead]),
      alertCreate: this.authorizationService.hasPermission([PERMISSIONS.CoreCompanyAlertCreate]),
      alertUpdate: this.authorizationService.hasPermission([PERMISSIONS.CoreCompanyAlertUpdate]),
      alertDelete: this.authorizationService.hasPermission([PERMISSIONS.CoreCompanyAlertDelete])
    };
  }

  private initPagination(): void {
    this.pagination = new Pagination({
      allowedRanges: CONFIG.pagination.allowedRanges,
      sort: 'company',
      direction: 'ASC'
    });
  }

  private translateAlertMsgs(): void {
    const messages = [
      'GENERICS.DELETE', 'GENERICS.CANCEL', 'GENERICS.DELETE_TITLE',
      'MESSAGES.CONFIRM_DELETE', 'GENERICS.UNASSIGN_TITLE',
      'GENERICS.UNASSIGN', 'MESSAGES.CONFIRM_UNASSIGN'
    ];
    this.translateService.get(messages, { value: 'Alert' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (translations) => this.translations = translations });
  }

  private buildParams(): any {
    const params: any = {
      page: this.pagination.page - 1,
      pagesize: this.pagination.pagesize,
      sort: this.pagination.sort,
      direction: this.pagination.direction,
      assignedtome: this.alertsToMe,
      createdbyme: this.alertsByMe
    };
    if (!this.showArchived) {
      params.processed = false;
    }
    if (this.companyId) {
      params.companyIds = this.companyId;
    }
    return params;
  }

  /**
   * Post-processes alert data to compute the display-friendly frequency name.
   * Handles both shapes: alerts with nested .alert property and flat alerts.
   */
  /**
   * Post-processes alert data to compute display-friendly frequency names
   * and pre-compute the isAssignedToMe flag for each alert.
   * Handles both shapes: alerts with nested .alert property and flat alerts.
   */
  private postProcessAlerts(alerts: Array<any>): Array<any> {
    if (!alerts) {
      return [];
    }
    alerts.forEach(alertItem => {
      if ('alert' in alertItem) {
        alertItem.alert.alertFrequencyName = this.getAlertFrequencyName(alertItem.alert.alertFrequency);
      } else {
        alertItem.alertFrequencyName = this.getAlertFrequencyName(alertItem.alertFrequency);
      }
      alertItem.isAssignedToMe = alertItem.alert?.users?.some(user => user.id === this.loggedUserId) ?? false;
    });
    return alerts;
  }
}
